import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, Car, User, Phone, Mail, Trash2, Check, X, Archive, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { testDriveApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function AdminTestDriveArchive() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [clearDays, setClearDays] = useState(30);

  const loadArchived = useCallback(async () => {
    try {
      const response = await testDriveApi.getArchivedBookings();
      setBookings(response.data || response || []);
    } catch (err) {
      console.error('Error loading archived bookings:', err);
      toast.error('Fehler beim Laden des Archivs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArchived();
  }, [loadArchived]);

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await testDriveApi.deleteBooking(deleteId);
      setBookings(bookings.filter(b => b.id !== deleteId));
      toast.success('Buchung gelöscht');
    } catch (err) {
      toast.error('Fehler beim Löschen');
    } finally {
      setDeleteId(null);
    }
  };

  const handleClearOld = async () => {
    try {
      const result = await testDriveApi.clearArchivedBookings(clearDays);
      toast.success(`${result.data?.deleted_count || 0} alte Buchungen gelöscht`);
      setShowClearDialog(false);
      loadArchived();
    } catch (err) {
      toast.error('Fehler beim Löschen');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'accepted':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded"><Check size={12} /> Bestätigt</span>;
      case 'rejected':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-red-100 text-red-700 rounded"><X size={12} /> Abgelehnt</span>;
      case 'pending':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-amber-100 text-amber-700 rounded"><Clock size={12} /> Ausstehend</span>;
      default:
        return <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">{status}</span>;
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gold"></div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto">
      {/* Header with Back Button */}
      <div className="mb-6">
        <Link 
          to="/admin/test-drive" 
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft size={18} />
          Zurück zum Kalender
        </Link>
        
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-3">
              <Archive className="text-muted-foreground" size={28} />
              Probefahrt-Archiv
            </h1>
            <p className="text-muted-foreground mt-1">
              {bookings.length} vergangene Buchungen
            </p>
          </div>
          
          {bookings.length > 0 && (
            <Button
              onClick={() => setShowClearDialog(true)}
              variant="outline"
              className="gap-2 text-red-600 border-red-200 hover:bg-red-50"
            >
              <Trash2 size={16} />
              Alte Einträge löschen
            </Button>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
        <AlertCircle size={20} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-blue-800">
          <strong>Info:</strong> Hier werden alle Probefahrt-Buchungen angezeigt, deren Datum bereits vergangen ist. 
          Diese werden automatisch archiviert.
        </div>
      </div>

      {/* Archived Bookings List */}
      {bookings.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <Archive size={48} className="mx-auto text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">Keine archivierten Buchungen</p>
        </div>
      ) : (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted/50 border-b border-border">
                <tr>
                  <th className="text-left p-4 font-medium text-sm">Datum</th>
                  <th className="text-left p-4 font-medium text-sm">Kunde</th>
                  <th className="text-left p-4 font-medium text-sm">Fahrzeug</th>
                  <th className="text-left p-4 font-medium text-sm">Status</th>
                  <th className="text-left p-4 font-medium text-sm">Kontakt</th>
                  <th className="text-center p-4 font-medium text-sm">Aktionen</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {bookings.map((booking) => (
                  <tr key={booking.id} className="hover:bg-muted/30 transition-colors">
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-muted-foreground" />
                        <span className="font-medium">{booking.date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                        <Clock size={12} />
                        <span>{booking.time_slot}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <User size={14} className="text-muted-foreground" />
                        <span>{booking.name}</span>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Car size={14} className="text-muted-foreground" />
                        <span>
                          {booking.car_info?.brand} {booking.car_info?.model}
                          {booking.car_info?.year && ` (${booking.car_info.year})`}
                        </span>
                      </div>
                    </td>
                    <td className="p-4">
                      {getStatusBadge(booking.status)}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <a 
                          href={`tel:${booking.phone}`} 
                          className="text-primary hover:text-primary/80"
                          title={booking.phone}
                        >
                          <Phone size={14} />
                        </a>
                        <a 
                          href={`mailto:${booking.email}`} 
                          className="text-primary hover:text-primary/80"
                          title={booking.email}
                        >
                          <Mail size={14} />
                        </a>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <button
                        onClick={() => setDeleteId(booking.id)}
                        className="p-2 text-muted-foreground hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Löschen"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Single Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Buchung löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Sind Sie sicher, dass Sie diese archivierte Buchung löschen möchten? 
              Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Clear Old Bookings Dialog */}
      <AlertDialog open={showClearDialog} onOpenChange={setShowClearDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alte Einträge löschen</AlertDialogTitle>
            <AlertDialogDescription>
              Alle archivierten Buchungen löschen, die älter als die angegebene Anzahl an Tagen sind.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium mb-2">
              Buchungen älter als (Tage):
            </label>
            <input
              type="number"
              value={clearDays}
              onChange={(e) => setClearDays(Math.max(1, parseInt(e.target.value) || 30))}
              min={1}
              className="w-full border border-border rounded-lg px-3 py-2"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleClearOld} className="bg-red-600 hover:bg-red-700">
              Löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
